import { KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { invert } from 'lodash';
import { useMemo, useState } from 'react';
import { Row } from 'react-table';

export const useReorderTableRows = (
  tableData,
  setTableData,
  rows: Row<any>[],
  prepareRow: (row: Row<any>) => void,
  onRowsReorder: (diffRows, newRows) => void
) => {
  const [activeId, setActiveId] = useState();

  // Items is an array of ids, used for SortableContext
  const items = useMemo(() => rows?.map(({ original }) => original.id), [rows]);
  const mappedItems = invert(Object.values(items));

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active?.id && over?.id && active.id !== over.id) {
      const initialTableData = [...tableData];

      // index of the item being dragged
      const oldIndex = Number(mappedItems[active.id]);

      // index of the item being dragged over
      const newIndex = Number(mappedItems[over.id]);

      let newTableData = arrayMove(tableData, oldIndex, newIndex);

      const minIndex = Math.min(oldIndex, newIndex);
      const maxIndex = Math.max(oldIndex, newIndex);

      // update line numbers for the items that were moved
      const diffTableData = newTableData.slice(minIndex, maxIndex + 1).map((item: any, index) => ({
        ...item,
        line: initialTableData[minIndex + index]?.line,
      }));

      // replace the items that were moved with the updated items
      newTableData = [
        ...newTableData.slice(0, minIndex),
        ...diffTableData,
        ...newTableData.slice(maxIndex + 1),
      ];

      setTableData(newTableData);

      try {
        await onRowsReorder(diffTableData, newTableData);
      } catch (error) {
        setTableData(initialTableData);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => setActiveId(null);

  const selectedReorderingRow = useMemo(() => {
    if (!activeId) {
      return null;
    }

    const row = rows.find(({ original }: any) => original.id === activeId);

    prepareRow(row);

    return row;
  }, [activeId, rows, prepareRow]);

  return {
    activeId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    selectedReorderingRow,
    sensors,
  };
};
